import { Button, Group, Loader, Modal, SegmentedControl, SimpleGrid, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";

import { getListActorsQueryKey, listActors } from "../api/generated/endpoints/generation/generation";
import type { AiActor, FirstAdPick, Voice } from "../api/generated/model";
import { UploadActor } from "../platform/actors/composer/UploadActor";
import { ErrorAlert } from "../platform/ErrorAlert";
import type { FirstAdDestination } from "../platform/first-ad";
import { VoicePanel } from "../platform/voices/VoicePanel";
import classes from "./first-ad-intake.module.css";
import { ThumbImage } from "../ui/ThumbImage";

export function FirstAdActor({ destination, pick, needsVoice, pending, onPick, onContinue }: {
  destination: FirstAdDestination; pick: FirstAdPick; needsVoice: boolean; pending: boolean;
  onPick: (actor: NonNullable<FirstAdPick["actor"]>, voice: string | null) => Promise<unknown>;
  onContinue: () => void;
}) {
  const { teamId, brandId } = destination;
  const [source, setSource] = useState("global");
  const [typed, setTyped] = useState("");
  const [search] = useDebouncedValue(typed.trim(), 300);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voice, setVoice] = useState<Pick<Voice, "id" | "kind"> | null>(null);
  const [error, setError] = useState<unknown>(null);
  const params = { kind: source === "team" ? "team" as const : "global" as const, search, limit: 24 };
  const actors = useInfiniteQuery({
    queryKey: [...getListActorsQueryKey(teamId, brandId, params), "first-ad-pages"],
    queryFn: ({ pageParam, signal }) => listActors(teamId, brandId, { ...params, ...(pageParam ? { after: pageParam } : {}) }, { signal }),
    initialPageParam: undefined as string | undefined, getNextPageParam: (page) => page.next ?? undefined,
    enabled: source !== "upload",
  });
  const choose = async (actor: AiActor) => {
    try { await onPick({ kind: actor.kind === "global" ? "global_actor" : "actor", actor_id: actor.id }, actor.voice_id); }
    catch (failure) { setError(failure); }
  };
  return <Stack aria-label="Actor selection">
    <SegmentedControl aria-label="Actor source" value={source} onChange={setSource}
      data={[{ value: "global", label: "Global AI Actors" }, { value: "team", label: "Your actors" }, { value: "upload", label: "Upload your own" }]} />
    {error || actors.error ? <ErrorAlert error={error ?? actors.error} /> : null}
    {source === "upload" ? <UploadActor teamId={teamId} brandId={brandId} above={null} models={[]}
      onStarted={() => {}} inline place={{ uploadMost: 1, onCreated: (made) => { if (made[0]) void choose(made[0]); } }} /> : <>
      <TextInput label="Search actors" maxLength={100} value={typed} onChange={(event) => setTyped(event.currentTarget.value)} />
      {actors.isPending ? <Loader aria-label="Loading actors" /> : null}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        {actors.data?.pages.flatMap((page) => page.items).map((actor) => <UnstyledButton key={actor.id}
          aria-label={`Choose ${actor.name}`} aria-pressed={pick.actor?.actor_id === actor.id} disabled={pending} onClick={() => void choose(actor)}>
          <ThumbImage full={actor.image_url} thumb={actor.thumb_url} alt="" className={classes.actorPicture} width={actor.width} height={actor.height} />
          <Text>{actor.name}{pick.actor?.actor_id === actor.id ? " ✓" : ""}</Text>
        </UnstyledButton>)}
      </SimpleGrid>
      {actors.hasNextPage ? <Button variant="subtle" loading={actors.isFetchingNextPage} onClick={() => void actors.fetchNextPage()}>More actors</Button> : null}
    </>}
    {pick.actor ? <Text size="sm">Your actor choice is saved.</Text> : null}
    {needsVoice ? <Group><Text size="sm">{pick.voice_id ? "Voice selected" : "Choose a voice for this actor"}</Text><Button variant="subtle" onClick={() => setVoiceOpen(true)}>Choose voice</Button></Group> : null}
    <Button size="lg" disabled={pending || !pick.actor || (needsVoice && !pick.voice_id)} loading={pending} onClick={onContinue}>Continue</Button>
    <Modal opened={voiceOpen} onClose={() => setVoiceOpen(false)} title="Your ad voice" size="lg">
      <VoicePanel teamId={teamId} brandId={brandId} chosen={voice} designModel={undefined} only={["global", "team"]}
        onClose={() => setVoiceOpen(false)} onChoose={(chosen) => {
          setVoice({ id: chosen.id, kind: chosen.kind });
          if (pick.actor) void onPick(pick.actor, chosen.id).then(() => setVoiceOpen(false)).catch(setError);
        }} />
    </Modal>
  </Stack>;
}
